import type { LngLatBounds } from 'maplibre-gl'
import type { MapGeoJSONFeature } from 'react-map-gl/maplibre'
import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import { filterInspectorFeaturesForMode } from '@/components/regionen/pageRegionSlug/modes/modeScopedSelection'
import { useCurrentMode } from '@/components/regionen/pageRegionSlug/modes/useCurrentMode'
import { useLayerControlsOpen } from '@/components/regionen/pageRegionSlug/SidebarLayerControls/layer-controls-store'

const boundsEqual = (current: LngLatBounds | null, next: LngLatBounds | null) => {
  if (current === null && next === null) return true
  if (current === null || next === null) return false
  return (
    current.getWest() === next.getWest() &&
    current.getSouth() === next.getSouth() &&
    current.getEast() === next.getEast() &&
    current.getNorth() === next.getNorth()
  )
}

const panelSizeEqual = (current: Store['inspectorSize'], next: Store['inspectorSize']) =>
  current.width === next.width && current.height === next.height

// INFO DEBUGGING: We could use a middleware to log state changes https://github.com/pmndrs/zustand#middleware
export type Store = StoreMapLoadedState &
  StoreMapDataLoadingState &
  StoreFeaturesInspector &
  StoreSizes &
  StoreInspectorUI &
  Actions

type StoreMapLoadedState = {
  mapLoaded: boolean
}

type StoreMapDataLoadingState = {
  mapDataLoading: boolean
  setFeatureStateLoading: boolean
}

type StoreSizes = {
  mapBounds: LngLatBounds | null
  inspectorSize: { width: number; height: number }
  sidebarSize: { width: number; height: number }
}

export type StoreFeaturesInspector = {
  // https://visgl.github.io/react-map-gl/docs/api-reference/types#mapgeojsonfeature
  inspectorFeatures: MapGeoJSONFeature[]
  // Where the map was clicked to pick `inspectorFeatures`; `null` when they come from the URL.
  inspectorClickLngLat: [lng: number, lat: number] | null
}

type StoreInspectorUI = {
  inspectorOtherPropertiesOpen: boolean
}

type Actions = {
  actions: {
    markMapLoaded: () => void
    startMapDataLoading: () => void
    finishMapDataLoading: () => void
    startFeatureStateSync: () => void
    finishFeatureStateSync: () => void
    updateMapBounds: (mapBounds: Store['mapBounds']) => void
    updateInspectorSize: (inspectorSize: Store['inspectorSize']) => void
    updateSidebarSize: (sidebarSize: Store['sidebarSize']) => void
    replaceInspectorFeatures: (args: {
      features: Store['inspectorFeatures']
      clickLngLat: Store['inspectorClickLngLat']
    }) => void
    clearInspectorFeatures: () => void
    setInspectorOtherPropertiesVisibility: (open: Store['inspectorOtherPropertiesOpen']) => void
  }
}

const useMapStore = create<Store>()((set) => {
  return {
    // Guards againt errors when using `mainMap?.getStyle`
    mapLoaded: false,
    // Toggels <LoadingIndicator>
    mapDataLoading: false,
    // Toggels <LoadingIndicator> for feature state updates
    setFeatureStateLoading: false,
    // Data for <Inspector> AND <LayerHighlight>
    inspectorFeatures: [],
    inspectorClickLngLat: null,
    mapBounds: null,
    inspectorSize: { width: 0, height: 0 },
    sidebarSize: { width: 0, height: 0 },
    inspectorOtherPropertiesOpen: false,
    actions: {
      markMapLoaded: () => set((state) => (state.mapLoaded ? state : { mapLoaded: true })),
      startMapDataLoading: () =>
        set((state) => (state.mapDataLoading ? state : { mapDataLoading: true })),
      finishMapDataLoading: () =>
        set((state) => (state.mapDataLoading === false ? state : { mapDataLoading: false })),
      startFeatureStateSync: () =>
        set((state) => (state.setFeatureStateLoading ? state : { setFeatureStateLoading: true })),
      finishFeatureStateSync: () =>
        set((state) =>
          state.setFeatureStateLoading === false ? state : { setFeatureStateLoading: false },
        ),
      replaceInspectorFeatures: ({ features, clickLngLat }) =>
        set({ inspectorFeatures: features, inspectorClickLngLat: clickLngLat }),
      clearInspectorFeatures: () =>
        set((state) =>
          state.inspectorFeatures.length === 0
            ? state
            : { inspectorFeatures: [], inspectorClickLngLat: null },
        ),
      updateMapBounds: (bounds) =>
        set((state) => (boundsEqual(state.mapBounds, bounds) ? state : { mapBounds: bounds })),
      updateInspectorSize: (size) =>
        set((state) =>
          panelSizeEqual(state.inspectorSize, size) ? state : { inspectorSize: size },
        ),
      updateSidebarSize: (size) =>
        set((state) => (panelSizeEqual(state.sidebarSize, size) ? state : { sidebarSize: size })),
      setInspectorOtherPropertiesVisibility: (open) =>
        set((state) =>
          state.inspectorOtherPropertiesOpen === open
            ? state
            : { inspectorOtherPropertiesOpen: open },
        ),
    },
  }
})

export const useMapLoaded = () => useMapStore((state) => state.mapLoaded)
export const useShowMapLoadingIndicator = () =>
  useMapStore((state) => state.mapDataLoading || state.setFeatureStateLoading)
export const useMapInspectorFeatures = () => {
  const features = useMapStore((state) => state.inspectorFeatures)
  const { mode } = useCurrentMode()
  return filterInspectorFeaturesForMode(features, mode)
}
export const useMapBounds = () => useMapStore((state) => state.mapBounds)
export const useMapInspectorSize = () => useMapStore((state) => state.inspectorSize)
const CLOSED_SIDEBAR_SIZE = { width: 0, height: 0 }

export const useMapSidebarSize = () => {
  const size = useMapStore((state) => state.sidebarSize)
  const open = useLayerControlsOpen()
  return open ? size : CLOSED_SIDEBAR_SIZE
}
export const useMapInspectorClickLngLat = () => useMapStore((state) => state.inspectorClickLngLat)
export const useMapInspectorOtherPropertiesOpen = () =>
  useMapStore((state) => state.inspectorOtherPropertiesOpen)

export const useMapDebugSnapshot = () =>
  useMapStore(
    useShallow((state) => ({
      mapLoaded: state.mapLoaded,
      mapDataLoading: state.mapDataLoading,
      setFeatureStateLoading: state.setFeatureStateLoading,
      mapBounds: state.mapBounds,
      inspectorSize: state.inspectorSize,
      sidebarSize: state.sidebarSize,
      inspectorFeatures: state.inspectorFeatures,
      inspectorOtherPropertiesOpen: state.inspectorOtherPropertiesOpen,
    })),
  )

export const useMapActions = () => useMapStore((state) => state.actions)
